import { useState } from 'react';
import { FileSpreadsheet, Plus, Trash2 } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  useCreateExpenseCategory,
  useDeleteExpenseCategory,
  useExpenseCategories,
  useUpdateExpenseCategory,
} from '../../hooks/useExpenseCategories';
import { EXPENSE_CATEGORIES_KEY } from '../../hooks/useExpenseCategories';
import { useCompanies } from '../../hooks/useCompanies';
import {
  clearFinancingCodes,
  getFinancingCodes,
  setFinancingCodes,
  type CategoryKind,
  type ExpenseCategory,
} from '../../lib/expenseCategoriesApi';
import { Select } from '../ui/Select';
import { ExcelImportDialog } from '../directory/ExcelImportDialog';

// Settings → "Expense Categories" (CLE-140). Editable nomenclator used by the
// bank-statement reconciliation. System categories can be renamed/recoloured and
// deactivated, but not deleted. Categories are per firm: the shared ones (no
// firm) are visible to every firm; the firm filter shows shared + that firm's,
// and a new category is created in the filtered firm (shared when none).

export function ExpenseCategoriesSettings() {
  const queryClient = useQueryClient();
  const { data: companies } = useCompanies();
  const [firmFilter, setFirmFilter] = useState('');
  const [importOpen, setImportOpen] = useState(false);
  const { data, isLoading, isError } = useExpenseCategories(true, firmFilter || undefined);
  const firmName = (id: string | null) => {
    if (!id) return 'Comună';
    const c = companies?.find((x) => x.id === id);
    return c ? c.displayName || c.legalName : '—';
  };
  // Financing categories (loans, administrator money, dividends, refunds): not
  // revenue or expense in the dashboard and reports. Per firm; with no firm
  // chosen, the default list every firm without its own list follows.
  const firmKey = firmFilter || null;
  const { data: financing } = useQuery({
    queryKey: ['financing-codes', firmKey],
    queryFn: () => getFinancingCodes(firmKey),
  });
  const financingCodes = financing?.codes;
  const onFinancingSaved = (
    firm: string | null,
    saved: Awaited<ReturnType<typeof getFinancingCodes>>,
  ) => {
    // Keyed by the firm the change was made for, not the one shown now.
    queryClient.setQueryData(['financing-codes', firm], saved);
    void queryClient.invalidateQueries({ queryKey: ['financing-codes'] });
    void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    void queryClient.invalidateQueries({ queryKey: ['financials'] });
  };
  const financingMutation = useMutation({
    mutationFn: (input: { firm: string | null; codes: string[] }) =>
      setFinancingCodes(input.firm, input.codes),
    onSuccess: (saved, input) => onFinancingSaved(input.firm, saved),
    onError: (err) => setError(err instanceof Error ? err.message : 'Salvarea nu a reușit.'),
  });
  const resetFinancing = useMutation({
    mutationFn: (firm: string) => clearFinancingCodes(firm),
    onSuccess: (saved, firm) => onFinancingSaved(firm, saved),
    onError: (err) => setError(err instanceof Error ? err.message : 'Salvarea nu a reușit.'),
  });
  function toggleFinancing(code: string, on: boolean) {
    const current = new Set(financingCodes ?? []);
    if (on) current.add(code);
    else current.delete(code);
    financingMutation.mutate({ firm: firmKey, codes: [...current] });
  }
  const createMutation = useCreateExpenseCategory();
  const updateMutation = useUpdateExpenseCategory();
  const deleteMutation = useDeleteExpenseCategory();

  const [newName, setNewName] = useState('');
  const [error, setError] = useState<string | null>(null);

  async function handleCreate() {
    const name = newName.trim();
    if (!name) return;
    setError(null);
    try {
      await createMutation.mutateAsync({ name, companyId: firmFilter || null });
      setNewName('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Categoria nu a putut fi creată.');
    }
  }

  async function handleRename(cat: ExpenseCategory, name: string) {
    if (!name.trim() || name === cat.name) return;
    setError(null);
    try {
      await updateMutation.mutateAsync({ id: cat.id, input: { name: name.trim() } });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Salvarea nu a reușit.');
    }
  }

  async function handleKind(cat: ExpenseCategory, kind: CategoryKind) {
    if (kind === cat.kind) return;
    setError(null);
    try {
      await updateMutation.mutateAsync({ id: cat.id, input: { name: cat.name, kind } });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Salvarea nu a reușit.');
    }
  }

  async function handleToggleActive(cat: ExpenseCategory) {
    setError(null);
    try {
      await updateMutation.mutateAsync({
        id: cat.id,
        input: { name: cat.name, isActive: !cat.isActive },
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Salvarea nu a reușit.');
    }
  }

  async function handleDelete(cat: ExpenseCategory) {
    setError(null);
    try {
      await deleteMutation.mutateAsync(cat.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ștergerea nu a reușit.');
    }
  }

  return (
    <div>
      {companies && companies.length > 1 && (
        <div className="mb-3 max-w-xs">
          <Select
            label="Firma"
            value={firmFilter}
            onChange={setFirmFilter}
            options={[
              { value: '', label: 'Toate firmele' },
              ...companies.map((c) => ({ value: c.id, label: c.displayName || c.legalName })),
            ]}
          />
        </div>
      )}
      {/* Phones: the name across the row, the two buttons as halves under it
          (side by side, the field shrank to a few letters). */}
      <div className="grid grid-cols-2 items-end gap-2 sm:flex">
        <label className="col-span-2 sm:flex-1">
          <span className="mb-1 block text-xs font-medium text-gray-600">Categorie nouă</span>
          <input
            type="text"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void handleCreate();
            }}
            placeholder="ex. Cloud / Hosting"
            className="w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
          />
        </label>
        <button
          type="button"
          onClick={() => void handleCreate()}
          disabled={createMutation.isPending || !newName.trim()}
          className="inline-flex items-center justify-center gap-1.5 rounded-md bg-brand-600 px-3 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50"
        >
          <Plus size={16} /> Adaugă
        </button>
        <button
          type="button"
          onClick={() => setImportOpen(true)}
          className="inline-flex items-center justify-center gap-1.5 rounded-md border border-brand-300 bg-white px-3 py-2 text-sm font-medium text-brand-700 hover:bg-brand-50"
        >
          <FileSpreadsheet size={16} /> Importă din Excel
        </button>
      </div>
      {companies && companies.length > 1 && (
        <p className="mt-1 text-xs text-gray-500">
          {firmFilter
            ? `Categoria nouă se creează doar pentru ${firmName(firmFilter)}.`
            : 'Fără firmă aleasă, categoria nouă e comună tuturor firmelor.'}
        </p>
      )}

      {importOpen && (
        <ExcelImportDialog
          entityType="expense-categories"
          onClose={() => setImportOpen(false)}
          onImportComplete={() =>
            void queryClient.invalidateQueries({ queryKey: EXPENSE_CATEGORIES_KEY })
          }
        />
      )}

      {error && (
        <div className="mt-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {error}
        </div>
      )}

      <div className="mt-4">
        {isLoading && <p className="text-sm text-gray-500">Se încarcă categoriile...</p>}
        {isError && <p className="text-sm text-red-600">Categoriile nu au putut fi încărcate.</p>}
        {data && (
          <>
            <p className="mb-2 text-xs text-gray-500">
              Coloana „Finanțare” marchează categoriile care nu sunt venituri sau cheltuieli
              (credite, împrumuturi de la administrator, dividende, rambursări): apar separat, la
              „Finanțare și distribuiri”.{' '}
              {firmKey
                ? financing?.inherited
                  ? 'Firma folosește lista implicită; prima bifă îi face o listă proprie.'
                  : 'Firma are o listă proprie.'
                : 'Fără firmă aleasă se editează lista implicită, folosită de firmele fără listă proprie.'}
              {firmKey && financing && !financing.inherited && (
                <button
                  type="button"
                  onClick={() => resetFinancing.mutate(firmKey)}
                  disabled={resetFinancing.isPending}
                  className="ml-2 font-medium text-brand-700 hover:underline"
                >
                  Revino la lista implicită
                </button>
              )}
            </p>
            <div className="overflow-x-auto rounded-lg border border-gray-200">
              {/* A floor width: on a phone the columns shrank until the
                  editable name showed a single letter; now the table scrolls
                  inside its card instead. */}
              <table className="min-w-[560px] divide-y divide-gray-200 text-sm sm:min-w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left font-medium text-gray-600">Nume</th>
                    <th className="px-3 py-2 text-left font-medium text-gray-600">Cod</th>
                    <th className="px-3 py-2 text-left font-medium text-gray-600">Tip</th>
                    <th
                      className="px-3 py-2 text-left font-medium text-gray-600"
                      title="Credite, împrumuturi de la administrator, dividende, rambursări: nu intră în venituri sau cheltuieli, apar separat la „Finanțare și distribuiri”."
                    >
                      Finanțare
                    </th>
                    {companies && companies.length > 1 && (
                      <th className="px-3 py-2 text-left font-medium text-gray-600">Firmă</th>
                    )}
                    <th className="px-3 py-2 text-left font-medium text-gray-600">Origine</th>
                    <th className="px-3 py-2 text-left font-medium text-gray-600">Status</th>
                    <th className="px-3 py-2 text-right font-medium text-gray-600">Acțiuni</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 bg-white">
                  {data.map((cat) => (
                    <tr key={cat.id} className="hover:bg-gray-50">
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-2">
                          {cat.color && (
                            <span
                              className="h-3 w-3 shrink-0 rounded-full"
                              style={{ backgroundColor: cat.color }}
                            />
                          )}
                          <input
                            type="text"
                            defaultValue={cat.name}
                            onBlur={(e) => void handleRename(cat, e.target.value)}
                            className="w-full min-w-[9rem] rounded border border-transparent px-1 py-0.5 text-sm hover:border-gray-200 focus:border-brand-500 focus:outline-none"
                          />
                        </div>
                      </td>
                      <td className="px-3 py-2 font-mono text-xs text-gray-500">{cat.code}</td>
                      <td className="px-3 py-2">
                        <select
                          aria-label={`Tipul categoriei ${cat.name}`}
                          value={cat.kind}
                          onChange={(e) => void handleKind(cat, e.target.value as CategoryKind)}
                          className="rounded border border-gray-200 px-1.5 py-0.5 text-xs"
                        >
                          <option value="EXPENSE">Cheltuială</option>
                          <option value="INCOME">Venit</option>
                        </select>
                      </td>
                      <td className="px-3 py-2">
                        <input
                          type="checkbox"
                          aria-label={`${cat.name}: finanțare, nu venit sau cheltuială`}
                          checked={(financingCodes ?? []).includes(cat.code)}
                          disabled={!financingCodes || financingMutation.isPending}
                          onChange={(e) => toggleFinancing(cat.code, e.target.checked)}
                          className="h-4 w-4 rounded border-gray-300 text-brand-600"
                        />
                      </td>
                      {companies && companies.length > 1 && (
                        <td className="px-3 py-2 text-xs text-gray-600">
                          {firmName(cat.companyId)}
                        </td>
                      )}
                      <td className="px-3 py-2">
                        <span
                          className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${
                            cat.isSystem ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'
                          }`}
                        >
                          {cat.isSystem ? 'Sistem' : 'Personalizat'}
                        </span>
                      </td>
                      <td className="px-3 py-2">
                        <button
                          type="button"
                          onClick={() => void handleToggleActive(cat)}
                          className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${
                            cat.isActive
                              ? 'bg-emerald-500/15 text-emerald-400'
                              : 'bg-gray-100 text-gray-500'
                          }`}
                        >
                          {cat.isActive ? 'Activ' : 'Inactiv'}
                        </button>
                      </td>
                      <td className="px-3 py-2 text-right">
                        {!cat.isSystem && (
                          <button
                            type="button"
                            onClick={() => void handleDelete(cat)}
                            title="Șterge"
                            className="rounded p-1 text-gray-400 hover:bg-red-50 hover:text-red-600"
                          >
                            <Trash2 size={16} />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
